// A CONNECTED WEBHOOK SOURCE (arc 101 doc 16) — one URL, one secret, one tenant.
//
// THE DEFECT THIS OBJECT EXISTS TO CLOSE. A signature proves the SOURCE, never
// the TENANT. The platform used to hold ONE signing secret per vendor
// (`SENTRY_CLIENT_SECRET` / `LINEAR_WEBHOOK_SECRET`), so every customer
// configuring their own Linear webhook held the same key, and the account+repo a
// delivery bound to was then read out of a NAME in the payload
// (`data.project.name` / `data.team.key`) matched against every enabled repo on
// the platform. The old rule — "exactly one match binds" — defends against
// AMBIGUITY (two tenants owning a repo of the same name). It does not defend
// against a HOSTILE SIGNER who names their project after someone else's repo on
// purpose. Those are different threats and only the first was handled.
//
// So: `POST /hooks/:source_id`. The id ROUTES (it selects which secret verifies
// the body) and the HMAC AUTHENTICATES; the tenant is `account_id` on this row
// and is never read from a payload. A party holding tenant A's secret can only
// ever write to tenant A — that is the whole slice, and every rule below is a
// consequence of it.
//
// THE ID IS NOT A BEARER SECRET. Urls leak into logs, proxies and browser
// history, so a leaked url must not be a grant. Knowing a source id buys nothing
// without the secret; that is why the id may be published on a connect screen.
//
// WHY `github` IS NOT A KIND. The App lane (`/gh/webhooks`) carries its own
// tenant identity — the installation id — so it needs no connection row, and a
// second door onto the same signal would be a branch nobody runs. The `sha256=`
// prefixed signature scheme GitHub uses is still expressible here (see
// {@link WebhookSignatureConfig.prefix}) and is covered by the generic tests, so
// nothing about the scheme is lost by leaving the kind out.

import { z } from 'zod';
import { AccountIdSchema, WebhookSourceIdSchema } from '../value-objects/ids';
import { RepoFullNameSchema } from '../value-objects/task-envelope';
import { ForgeMrObserveRefusalReasonSchema, ForgePrObserveRefusalReasonSchema } from './audit';
import { forgeProjectKind, GiteeRepoSchema } from './gitee-forge';
import { ForgeProjectSchema } from './gitlab-forge';

/**
 * Which MAPPER a delivery on this connection runs through. Closed by
 * construction (a `const` object plus a `z.enum` over its members — the
 * `OutcomeKind` idiom), and a ONE-WAY DOOR: the value is stored in
 * `webhook_sources.kind`, so a member removed later is unreadable rows.
 */
export const WebhookSourceKind = {
  /** Sentry issue / event alerts, keyed on the GROUP → ONE raw task. */
  Sentry: 'sentry',
  /** Linear `Issue` deliveries, keyed on the ticket → ONE raw task. */
  Linear: 'linear',
  /** Anything else, mapped by declared field paths → ONE raw task. */
  Generic: 'generic',
  /**
   * A gitlab.com project's merge request, pipeline and note deliveries (plan 101 slice
   * 07). Files NO raw task: each accepted delivery is a HINT naming a merge request,
   * which the worker re-reads with the source's own API token and records as one
   * `forge.mr.observed` audit row. Read-only — nothing is written back to GitLab. One of
   * the two kinds that carry `apiTokenRef` + `forgeProject` ({@link WebhookSourceSchema}).
   */
  Gitlab: 'gitlab',
  /**
   * A gitee.com repo's PR and PR-comment deliveries (plan 101 slice 10b), the GitLab lane
   * mirrored: each is a HINT naming a PR, re-read with the source's personal access token
   * into one `forge.pr.observed` row. Read-only; `forgeProject` is the Gitee arm.
   */
  Gitee: 'gitee',
} as const;
export const WebhookSourceKindSchema = z.enum(WebhookSourceKind);
export type WebhookSourceKind = z.infer<typeof WebhookSourceKindSchema>;

/**
 * `connected | error` — never a gate on verification. Connect sets `connected`; a
 * signing-secret rotation resets the row to `connected` unless it holds a
 * {@link WebhookSourceReadFailure} (#6180); TWO writers record a fault in `last_error`. Ingest's delivery-health
 * tracker names a delivery failure (`bad_signature`, `persist_failed`, …; plan 101
 * 04-12, `apps/ingest/src/sources.ts`), and a forge read lane (`gitlab.mr_hint`,
 * `gitee.pr_hint`) names a {@link WebhookSourceReadFailure} (#6156). Which writer may
 * CLEAR which value is the rule on {@link WEBHOOK_SOURCE_READ_FAILURES}.
 */
export const WebhookSourceStatus = {
  Connected: 'connected',
  Error: 'error',
} as const;
export const WebhookSourceStatusSchema = z.enum(WebhookSourceStatus);
export type WebhookSourceStatus = z.infer<typeof WebhookSourceStatusSchema>;

/**
 * The `last_error` values the forge READ owns (#6156): the `gitlab.mr_hint` and
 * `gitee.pr_hint` lanes' refusals that say the CONNECTION cannot read, stored verbatim
 * from `forge.mr.observe_refused` / `forge.pr.observe_refused`'s closed reasons. A stored
 * one-way door: rendered raw by `integration source_list`, never renamed, only added.
 *
 * Not in it: a per-MR/PR or per-hint refusal (`gitlab_not_found` / `gitee_not_found` —
 * one deleted MR or PR; `project_mismatch`, `repo_mismatch`), a producer bug
 * (`source_not_gitlab`, `source_not_gitee`), a row that is gone (`source_not_found`),
 * and `source_not_connected`, which is an existing error. `gitee_repo_not_found` IS in
 * it: a renamed or deleted repo fails every PR read the source will ever make.
 *
 * THE OWNERSHIP RULE. A delivery that VERIFIES at ingest says nothing about the API
 * token (the webhook's signing secret is another credential: a revoked project token
 * leaves every delivery verifying), so ingest's "verified → connected" clear must
 * never clear one of these — it would repaint the row `connected` on every delivery
 * while every read fails.
 * Only the lane's own next successful read clears one, and the lane never clears an
 * ingest value. A signing-secret rotation keeps one too (#6180): it replaces the OTHER
 * credential. All three are enforced in the SQL (`setWebhookSourceHealth`,
 * `setWebhookSourceReadHealth`, `rotateWebhookSourceSecret`, `packages/db`). An ingest
 * FAILURE may still replace one: the newest connection fault names the column, and the
 * lane names its own again on its next refused read.
 */
const GitlabReadFailureSchema = ForgeMrObserveRefusalReasonSchema.extract([
  'gitlab_unauthorized',
  'gitlab_forbidden',
  'api_token_unreadable',
  'api_token_missing',
]);
const GiteeReadFailureSchema = ForgePrObserveRefusalReasonSchema.extract([
  'gitee_unauthorized',
  'gitee_forbidden',
  'gitee_repo_not_found',
]);
export const WebhookSourceReadFailureSchema = z.union([
  GitlabReadFailureSchema,
  GiteeReadFailureSchema,
]);
export type WebhookSourceReadFailure = z.infer<typeof WebhookSourceReadFailureSchema>;
export const WEBHOOK_SOURCE_READ_FAILURES: readonly WebhookSourceReadFailure[] = [
  ...GitlabReadFailureSchema.options,
  ...GiteeReadFailureSchema.options,
];

/** True when a stored `last_error` is one a forge read owns ({@link WEBHOOK_SOURCE_READ_FAILURES}). */
export function isWebhookSourceReadFailure(
  lastError: string | null,
): lastError is WebhookSourceReadFailure {
  return WebhookSourceReadFailureSchema.safeParse(lastError).success;
}

// ─── the signature scheme ────────────────────────────────────────────────────

/**
 * ONE algorithm, declared rather than assumed. It is a stored field because the
 * shape has to carry it (a row that does not say how it was signed cannot be
 * re-verified after a second algorithm exists), and it is a one-member union
 * because a second member with no verifier behind it is a branch nobody runs.
 * Adding one is a code change with tests, not a config value.
 */
export const WebhookSignatureAlgorithm = { HmacSha256: 'hmac-sha256' } as const;
export const WebhookSignatureAlgorithmSchema = z.enum(WebhookSignatureAlgorithm);
export type WebhookSignatureAlgorithm = z.infer<typeof WebhookSignatureAlgorithmSchema>;

/** How the digest is spelled in the header. */
export const WebhookSignatureEncoding = { Hex: 'hex', Base64: 'base64' } as const;
export const WebhookSignatureEncodingSchema = z.enum(WebhookSignatureEncoding);
export type WebhookSignatureEncoding = z.infer<typeof WebhookSignatureEncodingSchema>;

/**
 * A header NAME, stored verbatim.
 *
 * Deliberately NOT lower-cased by the schema. It would have to be a `transform`,
 * which cannot be represented in JSON Schema — and this shape is published as an
 * MCP tool's `inputSchema`, so a transform anywhere in it breaks the catalog for
 * every verb. Nothing needs it: header lookup is case-insensitive by the fetch
 * spec (`Headers.get`), which is what every read of this value goes through.
 */
const HeaderNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/, 'expected an HTTP header name');

/**
 * The only per-vendor variation that matters: which header carries the digest,
 * over which algorithm, in which encoding, behind which prefix.
 *
 * Linear and Sentry send a BARE hex digest; GitHub sends `sha256=` + hex. Both
 * are this object with different fields, which is what lets one verifier serve
 * every source instead of one hand-written `verify*Signature` per vendor.
 */
export const WebhookSignatureConfigSchema = z.strictObject({
  header: HeaderNameSchema,
  algorithm: WebhookSignatureAlgorithmSchema,
  encoding: WebhookSignatureEncodingSchema,
  /** Literal prefix stripped before the compare (`sha256=`). Absent ⇒ bare digest. */
  prefix: z.string().max(32).optional(),
});
export type WebhookSignatureConfig = z.infer<typeof WebhookSignatureConfigSchema>;

/**
 * Header names that may NOT carry a source's idempotency key, lower-cased.
 *
 * The standard request headers plus the hop-by-hop set (RFC 9110 §7.6.1). What they
 * share is the only property that matters here: their value is constant, or
 * near-constant, across a sender's deliveries.
 */
export const DELIVERY_HEADER_DENYLIST: ReadonlySet<string> = new Set([
  // Constant per sender — the whole hazard.
  'accept',
  'accept-charset',
  'accept-encoding',
  'accept-language',
  'authorization',
  'content-encoding',
  'content-language',
  'content-length',
  'content-type',
  'cookie',
  'expect',
  'from',
  'host',
  'origin',
  'referer',
  'user-agent',
  // Hop-by-hop: set by proxies, not by the sender, so they are not the sender's key
  // even when they do vary.
  'connection',
  'keep-alive',
  'proxy-authenticate',
  'proxy-authorization',
  'te',
  'trailer',
  'transfer-encoding',
  'upgrade',
]);

/**
 * The delivery header, fenced.
 *
 * This value IS the idempotency key: `hooks.ts` scopes it per connection and it
 * becomes both the replay nonce and the `inbound_events.delivery_id`. Name a header
 * whose value never changes and the FIRST delivery claims that nonce for the whole
 * retention window — every later delivery is then a 409 whose own log line says
 * "stop replaying", sending a maintainer to debug a sender that is behaving
 * correctly. Self-inflicted, but the platform can see it at connect time, so
 * refusing here is the difference between a config error and a silent 14-day outage
 * reported as the customer's fault.
 *
 * A `refine` rather than the `transform` the {@link HeaderNameSchema} docblock rules
 * out: this shape is published as an MCP tool's `inputSchema`, and a refine is
 * JSON-Schema-representable where a transform is not. So the comparison lower-cases
 * a COPY and the stored value stays verbatim.
 *
 * Applied to the field, so it holds on the READ path too — a row that drifted to a
 * constant header is one whose every delivery 409s, and it must fail where it is
 * loaded rather than be acted on. Free to install today: `webhook_sources` held 0
 * rows in prod when this landed (2026-09-15, whole table, all time).
 *
 * `signature.header` is deliberately NOT fenced. A constant there fails SAFE — the
 * digest will not match and the delivery 401s on the spot — so the two headers have
 * different failure modes and only this one needs a fence.
 */
const DeliveryHeaderNameSchema = HeaderNameSchema.refine(
  (h) => !DELIVERY_HEADER_DENYLIST.has(h.toLowerCase()),
  {
    error: (iss) =>
      `'${String(iss.input).toLowerCase()}' cannot be the delivery header: its value is the ` +
      'same on every request, so the first delivery would claim that idempotency key and ' +
      'every later one would be refused as a replay. Name the header your sender stamps ' +
      'with a unique id per delivery (for example x-delivery-id).',
  },
);

// ─── field paths ─────────────────────────────────────────────────────────────

/** How deep a declared path may reach. A mapping is a projection, not a query language. */
export const WEBHOOK_PATH_MAX_SEGMENTS = 8;

/**
 * A dotted path into a delivery body: `data.issue.title`, `labels.0.name`.
 * Segments are literal object keys; a numeric segment indexes an array.
 *
 * Deliberately NOT JSONPath or an expression language. Anything a path cannot
 * express is a job for an MCP tool, not for a mapping DSL that grows a
 * conditional every quarter.
 */
export const WebhookPathSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9_$-]+(\.[A-Za-z0-9_$-]+)*$/, 'expected a dotted field path')
  .refine(
    (p) => p.split('.').length <= WEBHOOK_PATH_MAX_SEGMENTS,
    `at most ${WEBHOOK_PATH_MAX_SEGMENTS} segments`,
  );
export type WebhookPath = z.infer<typeof WebhookPathSchema>;

/**
 * Read a dotted path off an untrusted body. `null` unless every hop exists and
 * the value at the end is a primitive we can render — an object or an array is
 * NOT a value, and returning `[object Object]` for one would be worse than
 * finding nothing.
 *
 * Pure and total: the payload is whatever a customer's system POSTed.
 */
export function readWebhookPath(payload: unknown, path: string): string | null {
  const value = resolveWebhookPath(payload, path);
  if (typeof value === 'string') return value.trim() === '' ? null : value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return String(value);
  return null;
}

/** The raw node a path names (an array, an object, a primitive), or `undefined`. */
export function resolveWebhookPath(payload: unknown, path: string): unknown {
  let node: unknown = payload;
  for (const segment of path.split('.')) {
    if (node === null || node === undefined) return undefined;
    if (Array.isArray(node)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0) return undefined;
      node = node[index];
      continue;
    }
    if (typeof node !== 'object') return undefined;
    // OWN properties only. A parsed JSON body inherits `Object.prototype`, so a
    // declared path of `toString` or `constructor` would otherwise resolve to a
    // FUNCTION off the prototype chain rather than to nothing — a value no
    // delivery ever sent, reached by a path a maintainer could type by accident
    // and an attacker could rely on.
    if (!Object.hasOwn(node, segment)) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return node;
}

// ─── the claim gesture ───────────────────────────────────────────────────────

/**
 * HOW A HUMAN HANDS AN ITEM OVER. Receiving a delivery is not the same as being
 * asked to do the work, and conflating them is how connecting a tracker adopts
 * a whole backlog: the old Linear lane mapped EVERY `Issue` create/update to a
 * task, so pointing it at a workspace with 4,000 tickets filed 4,000 asks.
 *
 * The gesture is per CONNECTION, not global — a maintainer may run two Linear
 * workspaces with different conventions — and it is REQUIRED at connect, so
 * nothing is ever claimed implicitly.
 *
 * `always` is the one member that admits everything, and it is not a loophole:
 * it is an explicit, audited declaration that every delivery on THIS connection
 * is a handover, which is exactly true of a CI alarm or an error tracker.
 */
export const WebhookGestureKind = {
  /** Every delivery on this connection is a handover (a CI alarm, an error tracker). */
  Always: 'always',
  /** The value at `path` equals `equals`, case-insensitively (an assignee, a status). */
  FieldEquals: 'field_equals',
  /** The array at `path` contains `equals` — optionally reading `field` off each element (labels). */
  ListContains: 'list_contains',
} as const;

const GestureMatchValueSchema = z.string().trim().min(1).max(200);

export const WebhookGestureSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal(WebhookGestureKind.Always) }),
  z.strictObject({
    kind: z.literal(WebhookGestureKind.FieldEquals),
    path: WebhookPathSchema,
    equals: GestureMatchValueSchema,
  }),
  z.strictObject({
    kind: z.literal(WebhookGestureKind.ListContains),
    path: WebhookPathSchema,
    /** Property read off each element (`name` for a label object). Absent ⇒ the element itself. */
    field: z
      .string()
      .trim()
      .min(1)
      .max(60)
      .regex(/^[A-Za-z0-9_$-]+$/)
      .optional(),
    equals: GestureMatchValueSchema,
  }),
]);
export type WebhookGesture = z.infer<typeof WebhookGestureSchema>;

/** Case-insensitive, whitespace-insensitive equality — the comparison a human means. */
function sameText(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Did this delivery carry the gesture? Pure, total, and FAIL-CLOSED: a body that
 * does not contain the path does not match, so an un-gestured item is dropped
 * rather than admitted.
 */
export function gestureMatched(payload: unknown, gesture: WebhookGesture): boolean {
  switch (gesture.kind) {
    case WebhookGestureKind.Always:
      return true;
    case WebhookGestureKind.FieldEquals: {
      const value = readWebhookPath(payload, gesture.path);
      return value !== null && sameText(value, gesture.equals);
    }
    case WebhookGestureKind.ListContains: {
      const node = resolveWebhookPath(payload, gesture.path);
      if (!Array.isArray(node)) return false;
      return node.some((element) => {
        const value =
          gesture.field === undefined
            ? element
            : typeof element === 'object' && element !== null
              ? (element as Record<string, unknown>)[gesture.field]
              : undefined;
        return typeof value === 'string' && sameText(value, gesture.equals);
      });
    }
  }
}

/**
 * The gesture in one line — PUBLISHED on the connect screen and repeated
 * verbatim when the bot declines an un-gestured item. A convention nobody can
 * discover is not a convention, and two spellings of it (one on the screen, one
 * in the refusal) is how the two drift.
 */
export function webhookGestureSentence(gesture: WebhookGesture): string {
  switch (gesture.kind) {
    case WebhookGestureKind.Always:
      return 'Every delivery on this connection is treated as a handover.';
    case WebhookGestureKind.FieldEquals:
      return `To hand an item over, set \`${gesture.path}\` to "${gesture.equals}".`;
    case WebhookGestureKind.ListContains:
      return `To hand an item over, add "${gesture.equals}" to \`${gesture.path}\`${
        gesture.field === undefined ? '' : ` (matched on \`${gesture.field}\`)`
      }.`;
  }
}

// ─── the generic mapping ─────────────────────────────────────────────────────

/**
 * WHAT A `generic` DELIVERY BECOMES — declarative field paths, not code and not
 * a model call. The output is CAPPED at a raw task: an ask, the repo it is
 * about, the key that makes re-filing it a no-op, and links. Nothing here can
 * mint executable work, set a confidence, or choose a sink — those are triage's,
 * and a mapping that could decide them would be deciding what triage exists to
 * decide, from a config field.
 */
export const WebhookMappingSchema = z.strictObject({
  /** The ask, in prose. The one required field: an inlet with nothing to say files nothing. */
  ask: WebhookPathSchema,
  /**
   * `owner/repo` this delivery is about. Optional because a source may PIN one
   * repo on the row instead. Whatever it names is still checked against THIS
   * account's enabled repos — a payload never picks a tenant, and it does not
   * get to pick a repo outside the tenant either.
   */
  repo: WebhookPathSchema.optional(),
  /**
   * The source's stable id for the THING, never for the delivery. Keyed on a
   * delivery, a webhook that fires on every edit files an ask per edit.
   *
   * OPTIONAL for a sender whose every delivery IS new work and whose body has no
   * id for it (a status page's daily `{"status":"down"}`): absent, the DELIVERY
   * id keys the ask (`normalizeGenericHook`), so each delivery files one and its
   * redelivery files nothing. Declared, it is binding: one value files one ask,
   * ever, and a body without it is refused as a mapping that does not fit.
   */
  dedupe_key: WebhookPathSchema.optional(),
  /** Followable links. Anything that is not an `http(s)` url is dropped, not refused. */
  evidence_urls: z.array(WebhookPathSchema).max(10).default([]),
  /** Drop (audited, 200) when the value at `path` equals `equals`. */
  drop_when: z.strictObject({ path: WebhookPathSchema, equals: z.string().max(200) }).optional(),
});
export type WebhookMapping = z.infer<typeof WebhookMappingSchema>;

/** Sentry project routes, slug -> `owner/name`: connect, `source_update` and the row parse it. */
export const SentryProjectsSchema = z.record(z.string().regex(/^[a-z0-9_-]+$/), RepoFullNameSchema);
export type SentryProjects = z.infer<typeof SentryProjectsSchema>;
const SentryMappingSchema = z.strictObject({ projects: SentryProjectsSchema });
const SourceMappingSchema = z.union([WebhookMappingSchema, SentryMappingSchema]);
function mappingFitsKind(
  kind: WebhookSourceKind,
  mapping: z.infer<typeof SourceMappingSchema> | null,
): boolean {
  if (kind === WebhookSourceKind.Generic) return mapping !== null && 'ask' in mapping;
  if (kind === WebhookSourceKind.Sentry) return mapping === null || 'projects' in mapping;
  return mapping === null;
}

/** Does the mapping's `drop_when` predicate fire on this body? */
export function mappingDropped(payload: unknown, mapping: WebhookMapping): boolean {
  if (mapping.drop_when === undefined) return false;
  const value = readWebhookPath(payload, mapping.drop_when.path);
  return value !== null && sameText(value, mapping.drop_when.equals);
}

// ─── the connection's drop rules ─────────────────────────────────────────────

/**
 * A connection-wide DROP list (#5656): `{path, equals}` pairs in the
 * `drop_when` vocabulary, stored on the row and evaluated on each kind —
 * sentry, linear, `generic` alike — after the claim gesture and before any
 * mapping. What a rule names is expected noise (a health check, a resolved
 * alert): an audited 200 `dropped` / `expected_error`, no task.
 */
export const WEBHOOK_DROP_RULES_CAP = 20;

/** One rule: a dotted payload path and the value that means "do not act". */
export const WebhookDropRuleSchema = z.strictObject({
  path: WebhookPathSchema,
  equals: z.string().max(200),
});
export const WebhookDropRulesSchema = z
  .array(WebhookDropRuleSchema)
  .max(WEBHOOK_DROP_RULES_CAP, `at most ${WEBHOOK_DROP_RULES_CAP} drop rules`);
export type WebhookDropRules = z.infer<typeof WebhookDropRulesSchema>;

/** Does a rule fire on this body? Null rules and an empty list match nothing. */
export function dropMatched(payload: unknown, rules: WebhookDropRules | null): boolean {
  if (rules === null) return false;
  return rules.some((rule) => {
    const value = readWebhookPath(payload, rule.path);
    return value !== null && sameText(value, rule.equals);
  });
}

// ─── the row ─────────────────────────────────────────────────────────────────

/**
 * The vault name (`secrets.name`) a source's signing secret is sealed under —
 * the `integrationSecretName` idiom, one namespace so a drifted `secret_ref`
 * cannot address a `.maintainer.yml`-authored row.
 *
 * TWO slots, because rotation must not drop deliveries: `current` is what a new
 * delivery is expected to be signed with, `previous` is the outgoing secret the
 * verifier also tries until {@link WebhookSource.previousSecretExpiresAt}.
 */
export function webhookSourceSecretName(id: string, slot: 'current' | 'previous'): string {
  return slot === 'current' ? `webhook_source:${id}` : `webhook_source:${id}:prev`;
}

/**
 * The vault name a `gitlab` / `gitee` source's API token is sealed under — the token the
 * worker re-reads with (a GitLab project access token, `read_api`; a Gitee PAT). Same
 * `webhook_source:` namespace as {@link webhookSourceSecretName}, its own slot: it is
 * not a signing secret and is never rotated with one.
 */
export function webhookSourceApiTokenName(id: string): string {
  return `webhook_source:${id}:api_token`;
}

/** How long a rotated-out secret keeps verifying. Long enough to reconfigure a vendor console by hand. */
export const WEBHOOK_SECRET_ROTATION_WINDOW_MS = 24 * 60 * 60 * 1_000;

/** Bytes of entropy in a minted signing secret (rendered hex ⇒ 64 chars). */
export const WEBHOOK_SECRET_BYTES = 32;

/**
 * The kinds whose SENDER signs with a secret the VENDOR generates, so the platform
 * cannot mint the one that verifies — every kind but `generic`, which is why a
 * connect or rotate on one of these REQUIRES the caller's `vendor_secret`:
 *
 * - `sentry` — every delivery is signed (`sentry-hook-signature`) with the
 *   **Client Secret** of the Sentry internal integration (Sentry App). Sentry
 *   generates it; nobody can set it.
 * - `linear` — Linear generates the webhook's signing secret when the webhook is
 *   created, and signs `linear-signature` with it.
 * - `gitlab` — GitLab generates the hook's **signing token** (`whsec_<base64>`, a
 *   32-byte key) and signs `webhook-signature` with it (Standard Webhooks).
 * - `gitee` — the maintainer TYPES the hook's 签名密钥 into Gitee (signing mode; D2, D3),
 *   so the platform still cannot mint the one Gitee signs `X-Gitee-Token` with.
 *
 * `generic` is deliberately absent: a generic sender signs with a secret WE mint
 * and hand back once, so a caller-supplied value there would only weaken a
 * 32-byte random secret into whatever was typed.
 */
export type PresetWebhookSourceKind = Exclude<WebhookSourceKind, typeof WebhookSourceKind.Generic>;

/**
 * A vendor-issued signing secret as a caller hands it over. Trimmed, because a
 * pasted secret with a trailing newline is a different HMAC key and would 401
 * every delivery. The floor refuses an obvious placeholder (Sentry's Client
 * Secret is 64 hex chars); the ceiling bounds what is sealed.
 */
export const WebhookVendorSecretSchema = z.string().trim().min(16).max(512);

/**
 * The one shape a `gitlab` source's vendor secret may take: GitLab's signing token,
 * `whsec_` + the standard base64 of a 32-byte key ("whsec_<base64> format encoding a
 * 32-byte key", docs.gitlab.com/api/project_webhooks, read 2026-10-03). It IS the
 * Standard Webhooks key the ingest verifier strips and decodes (`verifyGitLabWebhook`,
 * `@developerz/gitlab`), so anything else seals a key no delivery verifies against:
 * the row reads `connected` while every delivery 401s. 32 bytes is 43 base64 characters
 * plus one optional `=`; pinning the length is what catches a truncated paste, which
 * would still decode.
 *
 * Kind-dependent, so it cannot live IN {@link WebhookVendorSecretSchema}: that is a
 * field schema, published as an MCP `inputSchema`, and never sees `kind`. The control
 * both connect and rotate read (`webhookSecretSource`, `apps/api`) applies it.
 */
export const GITLAB_SIGNING_TOKEN_PATTERN = /^whsec_[A-Za-z0-9+/]{43}=?$/;

/** The expected format, as a refusal names it. */
export const GITLAB_SIGNING_TOKEN_FORMAT =
  '`whsec_` followed by the base64 of a 32-byte key (the signing token GitLab shows once when the webhook is created)';

/** Deliveries one source may spend per minute. A noisy CI system cannot eat an account's whole intake. */
export const WEBHOOK_SOURCE_RATE_LIMIT = 120;
export const WEBHOOK_SOURCE_RATE_WINDOW_MS = 60_000;

/**
 * Per UTC day, the count a connected source is capped on: the `raw_tasks` rows
 * carrying its dedupe-key prefix — in practice its NEW asks
 * (`countWebhookAsksFiledSince`, `@developerz/db`, states the exact shape). A
 * repeat delivery on a dedupe key that is already a `raw_tasks` row does NOT
 * count.
 *
 * SOFT under concurrency, by design (#5655): the route reads the count, then
 * files, with no lock between — parallel deliveries can each read 19 and all
 * file. The overshoot is bounded only by the deliveries whose count read lands
 * before the 20th row commits. The per-minute limiter caps arrivals at
 * `WEBHOOK_SOURCE_RATE_LIMIT` per window — up to twice that across a window
 * boundary, and none while the limiter fails open — so it narrows the
 * overshoot without being a ceiling on it. Making the cap exact is a separate
 * change nobody has asked for: it needs a per-source `pg_advisory_xact_lock`
 * taken before the count, inside a transaction that also files — the hook lane
 * files through `createRawTask` on the pooled client, which opens no
 * transaction of its own, and under READ COMMITTED (the driver default) two
 * deliveries that each count 19 inside their own transactions both commit. The
 * pattern is `packages/db/src/queries/runners/capacity.ts`.
 *
 * Why a per-day number: an error tracker in a bad hour can produce hundreds of
 * distinct groups, and on a public repo each one would become public work. The
 * minute budget bounds the spike; this one bounds the pile. Enforced in `routes/hooks.ts`
 * before `fileRawTask` (#5655).
 */
export const WEBHOOK_INTAKE_DAILY_CAP = 20;

const WebhookSourceFieldsSchema = z.object({
  id: WebhookSourceIdSchema,
  /** THE TENANT. Resolved from this row, never from a payload. */
  accountId: AccountIdSchema,
  kind: WebhookSourceKindSchema,
  /** The maintainer's own name for it — several sources of one kind are normal. */
  label: z.string().trim().min(1).max(120),
  secretRef: z.string().min(1).max(200),
  /** The outgoing secret during a rotation window. Null in the steady state. */
  previousSecretRef: z.string().min(1).max(200).nullable(),
  previousSecretExpiresAt: z.date().nullable(),
  signature: WebhookSignatureConfigSchema,
  /** The header carrying this source's idempotency key. Fenced — see {@link DELIVERY_HEADER_DENYLIST}. */
  deliveryHeader: DeliveryHeaderNameSchema,
  /** Generic projection, optional Sentry project routes, otherwise null. */
  mapping: SourceMappingSchema.nullable(),
  /**
   * `gitlab` / `gitee` only — the vault name ({@link webhookSourceApiTokenName}) of the API
   * token the worker re-reads with. Platform-written at connect, like `secretRef`.
   */
  apiTokenRef: z.string().min(1).max(200).nullable(),
  /**
   * `gitlab` / `gitee` only — what it observes, resolved at connect: two disjoint strict arms
   * (D11) discriminated by the ROW's `kind` (the refine below), so the GitLab shape gains no
   * key. Read an arm through `gitlabProjectOf` / `giteeRepoOf`.
   */
  forgeProject: z.union([ForgeProjectSchema, GiteeRepoSchema]).nullable(),
  /** The connection's drop rules (#5656). Null — or empty — means none. */
  dropMatching: WebhookDropRulesSchema.nullable(),
  gesture: WebhookGestureSchema,
  /** Optional pin; Sentry's explicit project routes override this fallback. */
  repoFullName: RepoFullNameSchema.nullable(),
  status: WebhookSourceStatusSchema,
  lastError: z.string().max(500).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * Three invariants the shape cannot express, checked at every read so a drifted
 * row fails HERE rather than being acted on:
 *
 * 1. Generic requires a projection; Sentry permits project routes; other kinds take none.
 * 2. `apiTokenRef` and `forgeProject` are present exactly on `gitlab` and `gitee`,
 *    the same way, and `forgeProject` is the arm of the row's kind. A forge row
 *    missing either has no token to read with or no repo to check a delivery
 *    against; on any other kind they would be a credential and a repo nothing reads.
 * 3. The rotation pair moves together. A `previous_secret_ref` with no expiry is
 *    a retired secret that verifies forever — the opposite of a rotation.
 */
export const WebhookSourceSchema = WebhookSourceFieldsSchema.superRefine((row, ctx) => {
  if (!mappingFitsKind(row.kind, row.mapping)) {
    ctx.addIssue({
      code: 'custom',
      message: `mapping must match source kind '${row.kind}': generic requires field paths; sentry permits projects; other kinds require null`,
      path: ['mapping'],
    });
  }
  const wantsForge = row.kind === WebhookSourceKind.Gitlab || row.kind === WebhookSourceKind.Gitee;
  for (const field of ['apiTokenRef', 'forgeProject'] as const) {
    if (wantsForge && row[field] === null) {
      ctx.addIssue({
        code: 'custom',
        message: `a '${row.kind}' source requires ${field}`,
        path: [field],
      });
    }
    if (!wantsForge && row[field] !== null) {
      ctx.addIssue({
        code: 'custom',
        message: `${field} is only valid on a 'gitlab' or 'gitee' source`,
        path: [field],
      });
    }
  }
  if (wantsForge && row.forgeProject !== null && forgeProjectKind(row.forgeProject) !== row.kind) {
    ctx.addIssue({
      code: 'custom',
      message: `a '${row.kind}' source's forgeProject must be the ${row.kind} shape`,
      path: ['forgeProject'],
    });
  }
  if ((row.previousSecretRef === null) !== (row.previousSecretExpiresAt === null)) {
    ctx.addIssue({
      code: 'custom',
      message: 'previousSecretRef and previousSecretExpiresAt are set together or not at all',
      path: ['previousSecretExpiresAt'],
    });
  }
});
export type WebhookSource = z.infer<typeof WebhookSourceSchema>;

/**
 * What a CONNECT supplies. The id, both secret refs, the status and the
 * timestamps are the platform's — an inlet that could set `secretRef` could
 * point a connection at another account's vault entry.
 */
export const ConnectWebhookSourceSchema = WebhookSourceFieldsSchema.pick({
  accountId: true,
  kind: true,
  label: true,
  signature: true,
  deliveryHeader: true,
  mapping: true,
  dropMatching: true,
  gesture: true,
  repoFullName: true,
}).refine((row) => mappingFitsKind(row.kind, row.mapping), {
  path: ['mapping'],
  message:
    'mapping must match source kind: generic field paths, optional sentry projects, otherwise null',
});
export type ConnectWebhookSource = z.infer<typeof ConnectWebhookSourceSchema>;

/**
 * The wire defaults for a vendor whose signature scheme we already know
 * (`docs/idea/webhooks.md`). A maintainer connecting Linear should not have to
 * know that its digest is bare hex under `Linear-Signature`, and the two places
 * that would otherwise both state it — a dashboard form and this package —
 * cannot then disagree. `generic` has no default on purpose: there is nothing to
 * guess, and guessing would produce a connection that 401s every delivery.
 */
export const WEBHOOK_SOURCE_WIRE_DEFAULTS: Readonly<
  Record<PresetWebhookSourceKind, { signature: WebhookSignatureConfig; deliveryHeader: string }>
> = {
  [WebhookSourceKind.Sentry]: {
    signature: {
      header: 'sentry-hook-signature',
      algorithm: WebhookSignatureAlgorithm.HmacSha256,
      encoding: WebhookSignatureEncoding.Hex,
    },
    deliveryHeader: 'request-id',
  },
  [WebhookSourceKind.Linear]: {
    signature: {
      header: 'linear-signature',
      algorithm: WebhookSignatureAlgorithm.HmacSha256,
      encoding: WebhookSignatureEncoding.Hex,
    },
    deliveryHeader: 'linear-delivery',
  },
  // SHAPE-ONLY `signature`: it names GitLab's header and digest so the stored row is
  // honest, but the generic verifier (`verifyRotating`) signs the bare body, while
  // Standard Webhooks signs `{webhook-id}.{webhook-timestamp}.{body}` under the
  // `whsec_`-decoded key. Ingest therefore branches on kind BEFORE the generic
  // verifier and calls `verifyGitLabWebhook` (`@developerz/gitlab`). The delivery
  // header is `webhook-id`: GitLab keeps it across retries, where
  // `X-Gitlab-Webhook-UUID` is per hook and `X-Gitlab-Event-UUID` is shared by
  // recursive hooks — neither is a replay key.
  [WebhookSourceKind.Gitlab]: {
    signature: {
      header: 'webhook-signature',
      algorithm: WebhookSignatureAlgorithm.HmacSha256,
      encoding: WebhookSignatureEncoding.Base64,
      prefix: 'v1,',
    },
    deliveryHeader: 'webhook-id',
  },
  // SHAPE-ONLY too: `X-Gitee-Token` is base64 HMAC-SHA256 over `<X-Gitee-Timestamp>\n<secret>`,
  // never the body, so ingest calls `verifyGiteeWebhook` (`@developerz/gitee`) before the
  // generic verifier. Keyed on the signed ms timestamp (D5): Gitee sends no delivery id.
  [WebhookSourceKind.Gitee]: {
    signature: {
      header: 'x-gitee-token',
      algorithm: WebhookSignatureAlgorithm.HmacSha256,
      encoding: WebhookSignatureEncoding.Base64,
    },
    deliveryHeader: 'x-gitee-timestamp',
  },
};

/** The public path a connected source POSTs to. One place, so the connect leg and the router agree. */
export function webhookSourcePath(id: string): string {
  return `/hooks/${id}`;
}
